import isIneligibleTest from '@utils/isIneligibleTest'
import sleep from '@utils/sleep'
import submitData from '@utils/submitData'
import submitServiceData from '@utils/submitServiceData'
import isCapHeaCustomer from '@utils/isCapHeaCustomer'
import isEversourceGasLead from '@utils/isEversourceGasLead'
import urlPathSetter from '@utils/urlPathSetter'

/**
 * Navigate to the next step in the form flow.
 * Handles form submission on submit steps and ineligibility checks.
 *
 * Note: the Eligibility step (step index 1) is handled directly in App.jsx
 * and never reaches this function.
 *
 * @param {Object} values - Current form values
 * @param {Object} formActions - { setSubmitCount, setIsSubmitting }
 * @param {Object} currentStep - The active step component (with a .info property)
 * @param {Function} setDataWasSent - Marks the form as successfully submitted
 * @param {Function} setCurrentStepIndex - Updates the active step
 * @param {Function} setIsIneligible - Shows the ineligibility modal
 */
const goNextStep = async (
  values,
  { setSubmitCount, setIsSubmitting },
  currentStep,
  setDataWasSent,
  setCurrentStepIndex,
  setIsIneligible
) => {
  try {
    document.activeElement.blur()

    // ServiceTitan widget for the hvac-service path — opens in place, no step change
    if (values.path === 'hvac-service') {
      if (window._scheduler) {
        window._scheduler.show()
      } else {
        const script = document.createElement('script')
        script.src = 'https://embed.scheduler.servicetitan.com/scheduler-v1.js'
        script.setAttribute('data-api-key', 'atrfhvbtnwdklq8zptgwdit8')
        script.setAttribute('schedulerid', 'sched_1f300pbehm7p1c51z95uiusj')
        script.setAttribute('id', 'se-widget-embed')
        script.setAttribute('defer', 'true')
        script.setAttribute('data-auto-open', 'true')
        document.body.appendChild(script)
      }
      setSubmitCount(0)
      setIsSubmitting(false)
      return
    }

    setIsIneligible(false)
    const ineligibleReason = isIneligibleTest(values)

    if (ineligibleReason) {
      setIsIneligible(ineligibleReason)
      setSubmitCount(0)
      setIsSubmitting(false)
      return
    }

    // HVAC Service, CAP HEA and paused-gas HEA leads skip the Schedule step —
    // jump two ahead. Computed before submitting so the payload can be scrubbed.
    const skipsSchedule =
      currentStep.info.isSubmitStep &&
      ((values.path === 'hvac' && values.salesOrService === 'service') ||
        isCapHeaCustomer(values) ||
        isEversourceGasLead(values))

    // A schedule can survive from an earlier visit to the Schedule step (the user went
    // Back and changed an eligibility answer). Submitting it would book real capacity for
    // a cohort that must not be scheduled, so drop it for anyone skipping Schedule.
    const submitValues = skipsSchedule ? { ...values, schedule: undefined } : values

    // Submit data when the current step is a submit step
    if (currentStep.info.isSubmitStep) {
      if (process.env.NODE_ENV === 'production') {
        if (values.path === 'hvac' && values.salesOrService === 'service') {
          await submitServiceData(submitValues)
        } else {
          await submitData(submitValues)
        }
      } else {
        // Development: simulate network delay for HEA to catch async edge cases
        if (values.path === 'hvac' && values.salesOrService === 'service') {
          await submitServiceData(submitValues)
        } else if (values.path === 'hvac') {
          await submitData(submitValues)
        } else {
          await sleep()
          await submitData(submitValues)
        }
      }

      setDataWasSent(true)
    }

    setCurrentStepIndex((prev) => prev + (skipsSchedule ? 2 : 1))
    setSubmitCount(0)

    // URL path is updated after navigation so the browser history reflects the new step
    urlPathSetter({ ...values, step: skipsSchedule ? 'thankYou' : undefined })
  } catch (error) {
    console.error('Error in goNextStep:', error)
    alert(error.message || 'An error occurred. Please try again.')
  } finally {
    setIsSubmitting(false)
  }
}

export default goNextStep
